import React, { useState, useEffect, useReducer, useRef } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';

import useSearchStore from '../../_global/js/SearchStore';

import EnhancedSelectSearch from './EnhancedSelectSearch';
import FacetAccordion from '../../facet-rse/jsx/FacetAccordion';
import SearchFilterContainer from '../../filter-rse/jsx/SearchFilterContainer';
import AppliedFacetTag from './AppliedFacetTag';
import ClearFacetsButton from './ClearFacetsButton';

import {
    getFacetOptionLabelByKey,
    getQueryParameterValuesByKey,
    sanitizeFacetQueryValues,
} from '../../_global/js/searchHelpers';

import toKebabCase from '../../_global/js/stringToKebab';

/**
 * The number of results to display per page.
 * i.e. Funnelback "num_ranks"
 */
const RESULTS_PER_PAGE = 9;

/**
 * Search results sorting options
 */
const SORTING_OPTIONS = [
    {
        label: 'Recommended', // Relevance/Default
        value: '',
    },
    {
        label: 'Latest',
        value: 'dmetadateAvailable',
    },
    // {
    //     label: 'Date: Oldest first',
    //     value: 'metadateAvailable',
    // },
    // {
    //     label: 'Title: A-Z',
    //     value: 'title',
    // },
    // {
    //     label: 'Title: Z-A',
    //     value: 'dtitle',
    // },
];

/**
 * Search Facet accordions
 */
const SEARCH_FACET_OPTIONS = [
    {
        name: 'Resource Type',
        queryStringParamName: 'f.Resource Type|resourceType',
        urlEncodedFormat: 'f.Resource+Type%7CresourceType',
    },
    {
        name: 'Curriculum Area',
        queryStringParamName: 'f.Curriculum Area|curriculumArea',
        urlEncodedFormat: 'f.Curriculum+Area%7CcurriculumArea',
    },
    {
        name: 'Stage',
        queryStringParamName: 'f.Stage|stage',
        urlEncodedFormat: 'f.Stage%7Cstage',
    },
    {
        name: 'Year Level',
        queryStringParamName: 'f.Year Level|gradeLevel',
        urlEncodedFormat: 'f.Year+Level%7CgradeLevel',
    },
    {
        name: 'Learning Mode',
        queryStringParamName: 'f.Learning Mode|learningMode',
        urlEncodedFormat: 'f.Learning+Mode%7ClearningMode',
    },
];

/**
 * Alternate messages for the search input placeholder
 */
const SEARCH_INPUT_PLACEHOLDERS = {
    onBlur: 'SEARCH',
    onFocus: 'SEARCH RESOURCES',
};

/**
 * Controllers for cancelling pending XHR requests.
 *
 * @source https://www.npmjs.com/package/axios#cancellation
 */
let funnelbackSearchRequestController = null;
let funnelbackSuggestionRequestController = null;

/**
 * Reducer to update the Funnelback query parameters
 * in the local state
 *
 * @param {Object} state The current state of the userInputs
 * @param {Object} action The "type" and "payload" (optional) to apply to the query parameters
 *
 * @returns Object The new userInput data
 */
function updateFunnelbackQuery(state, action) {
    let updatedState = null;

    switch (action.type) {
        case 'update_state':
            /**
             * Expects an Array of two values:
             * [0] The Object Key/ID
             * [1] The new value
             */
            updatedState = {
                ...state,
                [action.payload[0]]: action.payload[1],
            };

            return updatedState;

        case 'update_query':
            /**
             * Keep any facets and sorting, but reset
             * the result count.
             *
             * Expects a payload of the new query String
             */
            updatedState = {
                ...state,
                num_ranks: RESULTS_PER_PAGE,
                query: action.payload,
            };

            return updatedState;

        case 'reset_query':
            /**
             * Keep any facets and sorting, but reset the query
             * and result count.
             */
            updatedState = {
                ...state,
                num_ranks: RESULTS_PER_PAGE,
                query: '',
            };

            return updatedState;

        default:
            console.log(
                'Error in ResourcesSearchPage.jsx. Unable to update Funnelback Query state data.'
            );
            return {
                ...state,
            };
    }
}

export default function ResourcesSearchPage({
    suggestionsUrl,
    searchUrl,
    lookupUrl,
    resultFallbackImageId,
}) {
    const searchPageURL = searchUrl;
    const [searchInputText, setSearchInputText] = useState('');
    const [searchInputHasFocus, setSearchInputHasFocus] = useState(false);
    const [searchSuggestions, setSearchSuggestions] = useState(null);
    const [searchResultsJson, setSearchResultsJson] = useState([]);
    const [searchSummaryJson, setSearchSummaryJson] = useState({});
    const [searchHasNoResults, setSearchHasNoResults] = useState(false);
    const [isRequestingSearchResults, setIsRequestingSearchResults] =
        useState(false);
    const [isRequestingSearchSuggestions, setIsRequestingSearchSuggestions] =
        useState(false);

    // Used to track the values selected for the Funnelback search
    const [funnelbackQueryParameters, dispatchFunnelbackQueryParametersUpdate] =
        useReducer(updateFunnelbackQuery, {
            num_ranks: RESULTS_PER_PAGE, // Set the default number of results
            query: '', // Set the default search term
        });

    const searchFacetsJson = useSearchStore((state) => state.searchFacetsJson);
    const setSearchFacetsJson = useSearchStore(
        (state) => state.setSearchFacetsJson
    );

    const searchFacetSelections = useSearchStore(
        (state) => state.searchFacetSelections
    );

    const searchSortingValue = useSearchStore(
        (state) => state.searchSortingValue
    );

    const setSearchSortingValue = useSearchStore(
        (state) => state.setSearchSortingValue
    );

    const setBatchSearchFacetSelectionsByKey = useSearchStore(
        (state) => state.setBatchSearchFacetSelectionsByKey
    );

    /**
     * Operations to perform when the search component first loads.
     */
    useEffect(() => {
        /**
         * Check the page query parameters for any search query
         * terms to "pre-apply" to the search results.
         */
        const queryString = window.location.href.split('?')[1] || '';
        const foundFacetQueryParameters = [];
        let foundSearchTermQueryParameters = [];
        let providedSearchTerm = '';
        let foundSortQueryParameters = [];
        let queryStringKeyValues = [];

        if (queryString !== '') {
            // Loop over the possible facet options
            for (let i = 0, j = SEARCH_FACET_OPTIONS.length; i < j; ++i) {
                queryStringKeyValues = sanitizeFacetQueryValues(
                    getQueryParameterValuesByKey(
                        queryString,
                        SEARCH_FACET_OPTIONS[i].urlEncodedFormat
                    )
                );

                if (queryStringKeyValues.length !== 0) {
                    // Capture the query key and it's value(s)
                    foundFacetQueryParameters.push({
                        queryStringParamName:
                            SEARCH_FACET_OPTIONS[i].queryStringParamName,
                        queryStringParamValue: queryStringKeyValues,
                    });
                }
            }

            // Check for a provided search term
            foundSearchTermQueryParameters = getQueryParameterValuesByKey(
                queryString,
                'query'
            );

            // Check for a provided sort option
            foundSortQueryParameters = getQueryParameterValuesByKey(
                queryString,
                'sort'
            );
        }

        if (foundFacetQueryParameters.length !== 0) {
            // Update the shared state with the found facet value(s)
            setBatchSearchFacetSelectionsByKey(foundFacetQueryParameters);
        }

        if (
            foundSearchTermQueryParameters.length !== 0 &&
            foundSearchTermQueryParameters[0] !== ''
        ) {
            providedSearchTerm = foundSearchTermQueryParameters[0].replace(
                /\+/g,
                ' '
            );

            // Update the Funnelback query with the provided search term
            dispatchFunnelbackQueryParametersUpdate({
                type: 'update_query',
                payload: providedSearchTerm,
            });

            // Update the search input area with the provided search term
            setSearchInputText(providedSearchTerm);
        }

        if (foundSortQueryParameters.length !== 0) {
            // Update the shared state with the provided sorting value
            setSearchSortingValue(foundSortQueryParameters[0]);
        }
    }, []);

    /**
     * Operations to perform when the Funnelback query is updated,
     * the sort option has changed, or when the Facet selections
     * have changed.
     */
    useEffect(() => {
        // Make the search request to update the search content
        // eslint-disable-next-line no-use-before-define
        getSearchResults();
    }, [funnelbackQueryParameters, searchFacetSelections, searchSortingValue]);

    // Testing state changes
    // useEffect(() => {
    //     console.log('Search results updated:', searchResultsJson);
    // }, [searchResultsJson]);
    // useEffect(() => {
    //     console.log('Search facets updated:', searchFacetsJson);
    // }, [searchFacetsJson]);
    // useEffect(() => {
    //     console.log('Search summary updated:', searchSummaryJson);
    // }, [searchSummaryJson]);

    /**
     * Respond to changes to the resources search input
     *
     * @param {Change Event} evt Event that triggered the input to update
     *
     * @returns void
     */
    const onSearchInputChange = (evt) => {
        // Trim any leading whitespace
        const inputValue = evt.target.value.replace(/^\s+/, '');

        // Capture the key press
        setSearchInputText(inputValue);

        // Cancel any pending suggestions request
        if (funnelbackSuggestionRequestController !== null) {
            funnelbackSuggestionRequestController.abort();
        }

        // Hide input loading indicator
        setIsRequestingSearchSuggestions(false);

        // Reset the search suggestions
        setSearchSuggestions(null);

        if (inputValue.length > 2) {
            // Request search suggestions
            // eslint-disable-next-line no-use-before-define
            getSearchSuggestions(inputValue);
        }
    };

    /**
     * Respond to when the resources search input loses focus
     *
     * @param {Blur Event} evt Event that triggered the input to update
     *
     * @returns void
     */
    const onSearchInputBlur = (evt) => {
        evt.stopPropagation();

        // Update the placeholder message and styling
        setSearchInputHasFocus(false);
    };

    /**
     * Respond to when the resources search input gets focus
     *
     * @param {Focus Event} evt Event that triggered the input to update
     *
     * @returns void
     */
    const onSearchInputFocus = (evt) => {
        evt.stopPropagation();

        // Update the placeholder message and styling
        setSearchInputHasFocus(true);
    };

    /**
     * Respond to clicks on the query completion suggestion buttons.
     *
     * @param {Click Event} evt Event that triggered the click
     *
     * @returns void
     */
    const onSuggestionButtonClick = (evt) => {
        const querySuggestion = evt.target.getAttribute('data-suggestion');
        evt.preventDefault();

        // Update the search input area with the suggestion
        setSearchInputText(querySuggestion);

        // Reset the search suggestions
        setSearchSuggestions(null);

        // Update the Funnelback query with the suggested search term
        dispatchFunnelbackQueryParametersUpdate({
            type: 'update_query',
            payload: querySuggestion,
        });
    };

    /**
     * Respond to clicks on button that clears the search input.
     *
     * @param {Click Event} evt Event that triggered the click
     *
     * @returns void
     */
    const onClearSearchButtonClick = (evt) => {
        evt.preventDefault();

        // Reset the search input area
        setSearchInputText('');

        // Reset the search suggestions
        setSearchSuggestions(null);

        // Trigger the search results to refresh
        dispatchFunnelbackQueryParametersUpdate({
            type: 'reset_query',
        });
    };

    /**
     * Respond to attempts to submit the search form.
     *
     * This is mostly used to capture when a user
     * submits a search query (even if it's empty).
     *
     * @param {Submit Event} evt Event that triggered the submission
     *
     * @returns void
     */
    const onFormSubmit = (evt) => {
        evt.preventDefault();

        // Cancel any pending suggestions request
        if (funnelbackSuggestionRequestController !== null) {
            funnelbackSuggestionRequestController.abort();
        }

        // Hide input loading indicator
        setIsRequestingSearchSuggestions(false);

        // Reset the search suggestions
        setSearchSuggestions(null);

        // Take the current search term and request results
        dispatchFunnelbackQueryParametersUpdate({
            type: 'update_query',
            payload: searchInputText,
        });
    };

    /**
     * Respond to clicks on the pagination button.
     *
     * Take the current number of results being displayed
     * and add another page count to it.
     *
     * @param {Click Event} evt Event that triggered the click
     *
     * @returns void
     */
    const onPaginationButtonClick = (evt) => {
        evt.preventDefault();

        // Update the Funnelback query with the number of results to return
        dispatchFunnelbackQueryParametersUpdate({
            type: 'update_state',
            payload: [
                'num_ranks',
                searchSummaryJson.numRanks + RESULTS_PER_PAGE,
            ],
        });
    };

    /**
     * Make a request to Funnelback (via Matrix proxy) to get
     * the search results for the currently set search term
     * and facets.
     *
     * @returns void
     */
    function getSearchResults() {
        const storedQueryParameters = {
            ...funnelbackQueryParameters,
        };
        const storedFacetSelections = {
            ...searchFacetSelections,
        };
        const fullQueryString = [];
        const currentPageUrl = window.location.href.split('?')[0];
        let encodedQueryParamaters = '';

        // Cancel any pending request for search results
        if (funnelbackSearchRequestController !== null) {
            funnelbackSearchRequestController.abort();
        }

        // Hide the no results message
        setSearchHasNoResults(false);

        // Show the loading message
        setIsRequestingSearchResults(true);

        // Create a new controller instance
        funnelbackSearchRequestController = new AbortController();

        // Combine the standard key/value pairs into a single query string
        Object.entries(storedQueryParameters).forEach((queryPair) => {
            const queryKey = queryPair[0];
            const queryValue = queryPair[1];

            fullQueryString.push(`${queryKey}=${queryValue}`);
        });

        // Include the "sort" value from the shared state
        if (searchSortingValue !== '') {
            fullQueryString.push(`sort=${searchSortingValue}`);
        }

        // Facets will be an Object with query keys and an Array of value(s)
        Object.entries(storedFacetSelections).forEach((facetQueryPair) => {
            const facetKey = facetQueryPair[0];
            const facetValues = sanitizeFacetQueryValues(facetQueryPair[1]);

            for (let i = 0, j = facetValues.length; i < j; ++i) {
                fullQueryString.push(`${facetKey}=${facetValues[i]}`);
            }
        });

        /**
         * Replace the current page URL so that it includes the
         * applied search parameters.
         *
         * This is so that search query is "remembered" if the user
         * navigates away from this page and then back again.
         */
        encodedQueryParamaters = encodeURI(fullQueryString.join('&')).replace(
            /%20/g,
            '+'
        );

        window.history.replaceState(
            {},
            '',
            `${currentPageUrl}?${encodedQueryParamaters}`
        );

        axios
            .get(`${lookupUrl}&${fullQueryString.join('&')}`, {
                signal: funnelbackSearchRequestController.signal,
            })
            .then((response) => {
                const responseData = response.data.response;

                // Clear the request controller
                funnelbackSearchRequestController = null;

                // Capture the search results
                setSearchResultsJson(responseData.resultPacket.results);

                // Capture the search facets
                setSearchFacetsJson(responseData.facets);

                // Capture the results summary
                setSearchSummaryJson(responseData.resultPacket.resultsSummary);

                // Hide the loading message
                setIsRequestingSearchResults(false);

                // If there are no results, show the no results message
                if (responseData.resultPacket.results.length === 0) {
                    setSearchHasNoResults(true);
                }
            })
            .catch((error) => {
                if (error.code === 'ERR_CANCELED') {
                    // The request was cancelled, do nothing.
                    return;
                }

                // Show the no results message
                setSearchHasNoResults(true);

                // Hide the loading message
                setIsRequestingSearchResults(false);
            });
    }

    /**
     * Make a request to Funnelback (via Matrix proxy) to get
     * search suggestions based on a provided partial query
     *
     * @param {String} partialQuery The search text to find suggestions for
     *
     * @returns void
     */
    function getSearchSuggestions(partialQuery) {
        // Show input loading indicator
        setIsRequestingSearchSuggestions(true);

        // Create a new controller instance
        funnelbackSuggestionRequestController = new AbortController();

        axios
            .get(`${suggestionsUrl}?query=${partialQuery}`, {
                signal: funnelbackSuggestionRequestController.signal,
            })
            .then((response) => {
                const responseData = response.data;
                const parsedSuggestions = [];

                // Clear the request controller
                funnelbackSuggestionRequestController = null;

                // Check if the response contains suggestions
                if (
                    Object.prototype.toString.call(responseData) !==
                    '[object Array]'
                ) {
                    // Hide the loading message
                    setIsRequestingSearchSuggestions(false);

                    // Error in the suggestions request
                    return;
                }

                for (let i = 0, j = responseData.length; i < j; ++i) {
                    parsedSuggestions.push({
                        id: `suggestion-${Math.ceil(Math.random() * 10000)}`,
                        label: responseData[i].disp,
                        value: responseData[i].key,
                    });
                }

                // Update the local state with any suggestions
                if (parsedSuggestions.length !== 0) {
                    setSearchSuggestions(parsedSuggestions);
                }

                // Hide the loading message
                setIsRequestingSearchSuggestions(false);
            })
            .catch((error) => {
                if (error.code === 'ERR_CANCELED') {
                    // The request was cancelled, do nothing.
                    return;
                }

                // Hide the loading message
                setIsRequestingSearchSuggestions(false);
            });
    }

    /**
     * Create the markup for any currently applied facet values.
     *
     * @returns JSX
     */
    function generateFacetTags() {
        const facetsData = [...searchFacetsJson];
        const selectedFacetsData = [];
        let facetQueryName = '';

        // Loop over each Facet
        for (let i = 0, j = facetsData.length; i < j; ++i) {
            // Reset the facet name
            facetQueryName = '';

            // Look for a matching facet
            for (let f = 0, q = SEARCH_FACET_OPTIONS.length; f < q; ++f) {
                if (SEARCH_FACET_OPTIONS[f].name === facetsData[i].name) {
                    // Capture the facet param name
                    facetQueryName =
                        SEARCH_FACET_OPTIONS[f].queryStringParamName;
                    break;
                }
            }

            if (facetQueryName !== '') {
                // Loop over each of the selected values (if any)
                for (
                    let b = 0, x = facetsData[i].selectedValues.length;
                    b < x;
                    ++b
                ) {
                    selectedFacetsData.push({
                        facetParamName: facetQueryName,
                        facetValue: facetsData[i].selectedValues[b].label,
                        id: `${toKebabCase(
                            facetsData[i].selectedValues[b].label
                        )}-${Math.ceil(Math.random() * 10000)}`,
                        label: getFacetOptionLabelByKey(
                            facetsData[i].selectedValues[b].label
                        ),
                    });
                }
            }
        }

        return selectedFacetsData.length !== 0 ? (
            <ul className="search-page-resources__applied-facets-list">
                {selectedFacetsData.map((facet) => {
                    return (
                        <li
                            className="search-page-resources__applied-facets-list-item"
                            key={facet.id}
                        >
                            <AppliedFacetTag
                                tagLabel={facet.label}
                                facetParamName={facet.facetParamName}
                                facetValue={facet.facetValue}
                            />
                        </li>
                    );
                })}
            </ul>
        ) : null;
    }

    function BRANDMARK_SVG() {
    /* eslint-disable prettier/prettier */
        return (
            <svg width="128" height="124" viewBox="0 0 128 124" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M19.4332 120.353C15.0251 124.75 7.87821 124.75 3.47013 120.353C-0.937957 115.957 -0.937957 108.828 3.47013 104.431L53.7538 54.278C58.1619 49.8813 65.3089 49.8813 69.7169 54.278C74.125 58.6746 74.125 65.803 69.7169 70.1997L19.4332 120.353Z" fill="#36B243"/>
                <path d="M73.6129 120.353C69.2048 124.75 62.0579 124.75 57.6498 120.353C53.2417 115.957 53.2417 108.828 57.6498 104.431L107.934 54.278C112.342 49.8813 119.489 49.8813 123.897 54.278C128.305 58.6746 128.305 65.803 123.897 70.1997L73.6129 120.353Z" fill="#FF6363"/>
                <path d="M69.7169 54.2752C74.125 58.6719 74.125 65.8003 69.7169 70.197C65.3088 74.5936 58.1619 74.5936 53.7538 70.197L3.47013 20.0435C-0.937953 15.6468 -0.937963 8.51839 3.47012 4.12172C7.8782 -0.274949 15.0251 -0.274948 19.4332 4.12172L69.7169 54.2752Z" fill="#FD98FF"/>
                <path d="M123.897 54.2752C128.305 58.6719 128.305 65.8003 123.897 70.197C119.489 74.5936 112.342 74.5936 107.934 70.197L57.6498 20.0435C53.2417 15.6468 53.2417 8.51839 57.6498 4.12172C62.0579 -0.274949 69.2048 -0.274948 73.6129 4.12172L123.897 54.2752Z" fill="#6688FF"/>
                <path fillRule="evenodd" clipRule="evenodd" d="M99.9492 62.2385L107.93 54.278C112.339 49.8813 119.485 49.8813 123.894 54.278C125.983 56.3624 127.082 59.0609 127.191 61.7912C127.311 64.8196 126.212 67.887 123.894 70.199C119.485 74.5957 112.338 74.5957 107.93 70.199L99.9492 62.2385Z" fill="#746FD9"/>
                <path fillRule="evenodd" clipRule="evenodd" d="M45.7695 62.2385L53.7507 54.278C58.1588 49.8813 65.3057 49.8813 69.7138 54.278C71.8166 56.3753 72.9163 59.0943 73.0129 61.8418C73.1186 64.8537 72.0189 67.8999 69.7138 70.199C65.3057 74.5957 58.1588 74.5957 53.7507 70.199L45.7695 62.2385Z" fill="#D18ED6"/>
            </svg>
        );
        /* eslint-enable prettier/prettier */
    }

    const searchContentRef = useRef(null);

    return (
        <div className="search-page-resources__parent-container">
            <form
                action={searchPageURL}
                className="search-page-resources__parent-row"
                method="get"
                onSubmit={onFormSubmit}
            >
                <div className="search-page-resources-mobile-filters__container">
                    <div className="search-page-resources--test-mobile-filters">
                        <SearchFilterContainer
                            sortSelectOptions={SORTING_OPTIONS}
                        >
                            {SEARCH_FACET_OPTIONS.map((facetData) => {
                                return (
                                    <FacetAccordion
                                        key={toKebabCase(facetData.name)}
                                        facetTitle={facetData.name}
                                        facetKey={facetData.name}
                                        facetParamName={
                                            facetData.queryStringParamName
                                        }
                                        searchContentRef={searchContentRef}
                                    />
                                );
                            })}
                        </SearchFilterContainer>
                    </div>
                </div>

                <div className="search-page-resources__container">
                    <div className="search-page-resources__row">
                        <div className="search-page-resources__column search-page-resources__sidebar col-lg-3-5 col-xxl-2-5">
                            <div className="search-page-resources__left-menu">
                                <fieldset>
                                    <div className="search-page-resources__left-menu-header">
                                        <legend className="search-page-resources__menu-title">
                                            Filter
                                        </legend>

                                        {Object.entries(searchFacetSelections)
                                            .length !== 0 ? (
                                            <ClearFacetsButton />
                                        ) : null}
                                    </div>

                                    {generateFacetTags()}

                                    <div className="search-page-resources__left-menu-facets">
                                        {SEARCH_FACET_OPTIONS.map(
                                            (facetData) => {
                                                return (
                                                    <FacetAccordion
                                                        key={toKebabCase(
                                                            facetData.name
                                                        )}
                                                        facetTitle={
                                                            facetData.name
                                                        }
                                                        facetKey={
                                                            facetData.name
                                                        }
                                                        facetParamName={
                                                            facetData.queryStringParamName
                                                        }
                                                        searchContentRef={
                                                            searchContentRef
                                                        }
                                                    />
                                                );
                                            }
                                        )}
                                    </div>
                                </fieldset>
                            </div>
                        </div>
                        <div
                            className="search-page-resources__column search-page-resources__column-results col-lg-8-5 col-lg-offset col-xxl-9-5"
                            ref={searchContentRef}
                        >
                            <div className="search-page-resources__query-section">
                                <EnhancedSelectSearch
                                    componentId="sort"
                                    selectOptions={SORTING_OPTIONS}
                                />

                                <div className="search-page-resources__search-container">
                                    {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                                    <label
                                        className="sr-only"
                                        htmlFor="resources-search-input"
                                    >
                                        Search the website for a resource
                                    </label>
                                    <input
                                        autoComplete="off"
                                        className={`search-page-resources__search-input ${
                                            searchInputHasFocus ||
                                            searchInputText !== ''
                                                ? 'is-expanded'
                                                : ''
                                        }`}
                                        id="resources-search-input"
                                        onBlur={onSearchInputBlur}
                                        onChange={onSearchInputChange}
                                        onFocus={onSearchInputFocus}
                                        placeholder={
                                            searchInputHasFocus
                                                ? SEARCH_INPUT_PLACEHOLDERS.onFocus
                                                : SEARCH_INPUT_PLACEHOLDERS.onBlur
                                        }
                                        spellCheck="false"
                                        type="text"
                                        value={searchInputText}
                                    />

                                    {isRequestingSearchSuggestions ? (
                                        <div className="search-page-resources__input-loading-wrapper">
                                            <span className="sr-only">
                                                Looking for search suggestions
                                            </span>
                                            <div
                                                title="Looking for search suggestions"
                                                className="search-page-resources__input-loading-animation"
                                            />
                                        </div>
                                    ) : null}

                                    <button
                                        className="search-page-resources__search-submit"
                                        type="submit"
                                    >
                                        {/* eslint-disable-next-line prettier/prettier */}
                                        <span className="sr-only">Submit search</span>
                                        <svg className="svg-icon">
                                            <use href="#rse-search-page-glass" />
                                        </svg>
                                    </button>

                                    {searchInputText !== '' ? (
                                        <button
                                            className="search-page-resources__search-clear"
                                            onClick={onClearSearchButtonClick}
                                            type="button"
                                        >
                                            {/* eslint-disable-next-line prettier/prettier */}
                                            <span className="sr-only">Clear search</span>
                                            <svg className="svg-icon">
                                                <use href="#rse-close" />
                                            </svg>
                                        </button>
                                    ) : null}

                                    {searchSuggestions ? (
                                        <ol className="search-page-resources__search-suggestions-list">
                                            {searchSuggestions.map(
                                                (suggestion) => {
                                                    return (
                                                        <li
                                                            className="search-page-resources__search-suggestions-list-item"
                                                            key={suggestion.id}
                                                        >
                                                            <button
                                                                className="search-page-resources__search-suggestions-option"
                                                                data-suggestion={
                                                                    suggestion.value
                                                                }
                                                                onClick={
                                                                    onSuggestionButtonClick
                                                                }
                                                                type="button"
                                                            >
                                                                {
                                                                    suggestion.label
                                                                }
                                                            </button>
                                                        </li>
                                                    );
                                                }
                                            )}
                                        </ol>
                                    ) : null}
                                </div>

                                <p className="search-page-resources__results-summary">
                                    {/* eslint-disable-next-line prettier/prettier */}
                                    {searchSummaryJson.totalMatching} resources found
                                </p>

                                <div className="search-page-resources__tags-mobile">
                                    {generateFacetTags()}
                                </div>
                            </div>

                            <div className="search-page-resources__results-container">
                                {searchResultsJson.length !== 0 ? (
                                    <>
                                        {/* eslint-disable prettier/prettier */}
                                        <ol className="search-page-resources__results-list">
                                            {searchResultsJson.map((result) => {
                                                let tilePalette = 'orange';
                                                let resultStage = [];
                                                let resultYear = [];
                                                let resultCurriculumArea = [];
                                                let resultLearningMode = [];
                                                let resultImageId = resultFallbackImageId;
                                                let resultPartnerImageId = '';
                                                let resultCardImageType = '';
                                                let resultCardType = '';

                                                // Set the image type
                                                if (result.listMetadata.resourceCardImageType) {
                                                    resultCardImageType = result.listMetadata.resourceCardImageType[0];
                                                }

                                                // Set the card type
                                                if (result.listMetadata.resourceTileCardType) {
                                                    resultCardType = result.listMetadata.resourceTileCardType[0];
                                                }

                                                // Set the result image
                                                if (result.listMetadata.resourceTileImageId) {
                                                    resultImageId = result.listMetadata.resourceTileImageId[0];
                                                }

                                                // Set the result partner logo image
                                                if (result.listMetadata.resourcePartnerLogoImageId) {
                                                    resultPartnerImageId = result.listMetadata.resourcePartnerLogoImageId[0];
                                                }

                                                /**
                                                 * Capture the metadata for the tags.
                                                 * Note: We only want to capture some metadata,
                                                 * and in the specific order below.
                                                 */
                                                if (result.listMetadata.stage) {
                                                    // Capture the result stage value(s) as we use those below.
                                                    resultStage = result.listMetadata.stage;
                                                }

                                                if (result.listMetadata.gradeLevel) {
                                                    // Capture the result stage value(s) as we use those below.
                                                    resultYear = result.listMetadata.gradeLevel;
                                                }

                                                if (result.listMetadata.learningMode) {
                                                    resultLearningMode = result.listMetadata.learningMode
                                                }

                                                if (
                                                    result.listMetadata.curriculumArea
                                                ) {
                                                    resultCurriculumArea = result.listMetadata.curriculumArea
                                                }

                                                // Set the colour theme
                                                if (resultStage.length === 1) {
                                                    if (
                                                        resultStage[0] ===
                                                        'stageEarlyChildhood'
                                                    ) {
                                                        tilePalette = 'pink';
                                                    }
                                                    if (
                                                        resultStage[0] ===
                                                        'stagePrimarySchool'
                                                    ) {
                                                        tilePalette = 'light-green';
                                                    }
                                                    if (
                                                        resultStage[0] ===
                                                        'stageSecondarySchool'
                                                    ) {
                                                        tilePalette = 'red';
                                                    }
                                                    if (
                                                        resultStage[0] ===
                                                        'stageLearnerNoviceDriver'
                                                    ) {
                                                        tilePalette = 'light-blue';
                                                    }
                                                }

                                                return (
                                                    <li
                                                        className="search-page-resources__results-list-item"
                                                        key={`result-${result.rank}`}
                                                    >
                                                        <a
                                                            className={`resource-tile__container ${resultCardType}`}
                                                            href={result.clickTrackingUrl}
                                                        >
                                                            <div
                                                                className={`resource-tile theme--${tilePalette}`}
                                                            >
                                                                <div className="resource-tile__front">
                                                                    <div className="resource-tile__front-top">
                                                                        <div className="resource-tile__content">
                                                                            {resultPartnerImageId !== '' ? (
                                                                                <div className="resource-tile__logo">
                                                                                    <img
                                                                                        className="svg-icon resource-tile__logo-icon"
                                                                                        alt=""
                                                                                        // src={`https://www-tac-stage.squiz.cloud?a=${resultPartnerImageId}`}
                                                                                        src={`./?a=${resultPartnerImageId}`}
                                                                                    />
                                                                                </div>
                                                                            ) : null}
                                                                            <div
                                                                                className={`resource-tile__image-container ${resultCardImageType}`}
                                                                            >
                                                                                <div className="resource-tile__image-position position--center">
                                                                                    <img
                                                                                        className="resource-tile__image"
                                                                                        // src={`https://www-tac-stage.squiz.cloud?a=${resultImageId}`}
                                                                                        src={`./?a=${resultImageId}`}
                                                                                        alt=""
                                                                                    />
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    </div>

                                                                    <div className="resource-tile__front-bottom">
                                                                        <div className="resource-tile__content">
                                                                            <div>
                                                                            <h2 className="resource-tile__title front--title">
                                                                                    {result.title}
                                                                                </h2>
                                                                                {result.listMetadata.resourceDescription && (
                                                                                    <p className="resource-tile__text front--text">
                                                                                        {result.listMetadata.resourceDescription[0]}
                                                                                    </p>
                                                                                )}
                                                                                {result.listMetadata.resourceType && (
                                                                                    <span className="resource-tile__category front--category">
                                                                                        {getFacetOptionLabelByKey(
                                                                                            result.listMetadata.resourceType[0]
                                                                                        )}
                                                                                    </span>
                                                                                )}
                                                                            </div>
                                                                            <div className="resource-tile__link front--link">
                                                                                <span className="resource-tile__link-text">
                                                                                    Visit Resource
                                                                                </span>
                                                                                <svg className="svg-icon resource-tile__arrow-icon resource-tile__arrow-long-icon">
                                                                                    <use href="#rse-arrow-right" />
                                                                                </svg>
                                                                                <svg className="svg-icon resource-tile__arrow-icon resource-tile__arrow-bold-icon">
                                                                                    <use href="#rse-arrow-right-bold" />
                                                                                </svg>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </div>

                                                                <div className="resource-tile__back">
                                                                    <div className="resource-tile__back-top">
                                                                        <h2 className="resource-tile__title back--title">
                                                                                    {result.title}
                                                                        </h2>
                                                                        {/* <div className="resource-tile__tag-row">
                                                                            {resultMetadata.map(
                                                                                (
                                                                                    metadataKey
                                                                                ) => {
                                                                                    return (
                                                                                        <span
                                                                                            className="pill"
                                                                                            key={`result-tag-${toKebabCase(metadataKey)}`}
                                                                                        >
                                                                                            {getFacetOptionLabelByKey(
                                                                                                metadataKey
                                                                                            )}
                                                                                        </span>
                                                                                    );
                                                                                }
                                                                            )}
                                                                        </div> */}
                                                                    </div>

                                                                    <div className="resource-tile__back-bottom">
                                                                        <div className="resource-tile__content">
                                                                            <div>
                                                                                {resultStage.length > 0 && (
                                                                                  <div className='stage'>
                                                                                    <p>Stage</p>
                                                                                    <div>
                                                                                        {resultStage.map((metadataKey, index) => {
                                                                                          const isLastIndex = index === resultStage.length - 1;
                                                                                          if (isLastIndex) {
                                                                                              return (
                                                                                                  <span
                                                                                                      className=""
                                                                                                      key={`result-tag-${toKebabCase(metadataKey)}`}
                                                                                                  >
                                                                                                      {getFacetOptionLabelByKey(metadataKey)}
                                                                                                  </span>
                                                                                              );
                                                                                          }

                                                                                          return <span key={`result-tag-${toKebabCase(metadataKey)}`}>{`${getFacetOptionLabelByKey(metadataKey)},`}</span>;
                                                                                      })}
                                                                                    </div>

                                                                                </div>
                                                                              )}
                                                                                
                                                                              {resultYear.length > 0 && (
                                                                                <div className='year-level'>
                                                                                    <p>Year Level</p>
                                                                                    <div>
                                                                                        {resultYear.map((metadataKey, index) => {
                                                                                          const isLastIndex = index === resultYear.length - 1;
                                                                                          if (isLastIndex) {
                                                                                              return (
                                                                                                  <span
                                                                                                      className=""
                                                                                                      key={`result-tag-${toKebabCase(metadataKey)}`}
                                                                                                  >
                                                                                                      {getFacetOptionLabelByKey(metadataKey)}
                                                                                                  </span>
                                                                                              );
                                                                                          }

                                                                                          return <span key={`result-tag-${toKebabCase(metadataKey)}`}>{`${getFacetOptionLabelByKey(metadataKey)},`}</span>;
                                                                                      })}
                                                                                    </div>
                                                                                </div>
                                                                              )}
                                                                                
                                                                              {resultCurriculumArea.length > 0 && (
                                                                                <div className='cirriculum-areas'>
                                                                                    <p>Curriculum Areas</p>
                                                                                    <div>
                                                                                        {resultCurriculumArea.map((metadataKey, index) => {
                                                                                          const isLastIndex = index === resultCurriculumArea.length - 1;
                                                                                          if (isLastIndex) {
                                                                                              return (
                                                                                                  <span
                                                                                                      className=""
                                                                                                      key={`result-tag-${toKebabCase(metadataKey)}`}
                                                                                                  >
                                                                                                      {getFacetOptionLabelByKey(metadataKey)}
                                                                                                  </span>
                                                                                              );
                                                                                          }

                                                                                          return <span key={`result-tag-${toKebabCase(metadataKey)}`}>{`${getFacetOptionLabelByKey(metadataKey)},`}</span>;
                                                                                      })}
                                                                                    </div>
                                                                                </div>
                                                                              )}
                                                                              
                                                                              {resultLearningMode.length > 0 && (
                                                                                <div className='learning-mode'>
                                                                                    <p>Learning Mode</p>
                                                                                    <div>
                                                                                        {resultLearningMode.map((metadataKey, index) => {
                                                                                          const isLastIndex = index === resultLearningMode.length - 1;
                                                                                          if (isLastIndex) {
                                                                                              return (
                                                                                                  <span
                                                                                                      className=""
                                                                                                      key={`result-tag-${toKebabCase(metadataKey)}`}
                                                                                                  >
                                                                                                      {getFacetOptionLabelByKey(metadataKey)}
                                                                                                  </span>
                                                                                              );
                                                                                          }

                                                                                          return <span key={`result-tag-${toKebabCase(metadataKey)}`}>{`${getFacetOptionLabelByKey(metadataKey)},`}</span>;
                                                                                      })}
                                                                                    </div>
                                                                                </div>
                                                                              )}
                                                                                
                                                                            </div>
                                                                            <div className="resource-tile__link">
                                                                                <span className="resource-tile__link-text">
                                                                                    {result.listMetadata.resourceType && (
                                                                                        getFacetOptionLabelByKey(result.listMetadata.resourceType[0])
                                                                                    )}
                                                                                     
                                                                                </span>
                                                                                <svg className="svg-icon resource-tile__arrow-icon">
                                                                                    <use href="#rse-arrow-right" />
                                                                                </svg>
                                                                                <svg className="svg-icon resource-tile__arrow-icon resource-tile__arrow-bold-icon">
                                                                                    <use href="#rse-arrow-right-bold" />
                                                                                </svg>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </a>
                                                    </li>
                                                );
                                            })}
                                        </ol>
                                        {/* eslint-enable prettier/prettier */}

                                        {searchSummaryJson.nextStart ? (
                                            <button
                                                className="search-page-resources__pagination-button"
                                                onClick={
                                                    onPaginationButtonClick
                                                }
                                                type="button"
                                            >
                                                View more
                                                <svg className="svg-icon">
                                                    <use
                                                        xlinkHref="#rse-chevron-down-16"
                                                        fill="none"
                                                    />
                                                </svg>
                                            </button>
                                        ) : null}
                                    </>
                                ) : null}

                                {/* eslint-disable prettier/prettier */}
                                {searchHasNoResults ? (
                                    <div className="search-page-resources__no-results-wrapper">
                                        <div className="search-page-resources__no-results-title-container">
                                            <div className="search-page-resources__no-results-brandmark-left">
                                                {BRANDMARK_SVG()}
                                            </div>
                                            <h2 className="search-page-resources__no-results-title">No results found</h2>
                                            <div className="search-page-resources__no-results-brandmark-right">
                                                {BRANDMARK_SVG()}
                                            </div>
                                        </div>
                                        <p className="search-page-resources__no-results-description">
                                            Try searching for something more general, change the filters or check for spelling mistakes.
                                        </p>
                                    </div>
                                ) : null}
                                {/* eslint-enable prettier/prettier */}

                                {isRequestingSearchResults ? (
                                    <div className="search-page-resources__result-loading-wrapper">
                                        <div className="search-page-resources__result-loading-container">
                                            <div className="search-page-resources__result-loading-animation" />
                                            <p className="search-page-resources__result-loading-message">
                                                Retrieving resources
                                            </p>
                                        </div>
                                    </div>
                                ) : null}
                            </div>
                        </div>
                    </div>
                </div>
            </form>
        </div>
    );
}

ResourcesSearchPage.propTypes = {
    suggestionsUrl: PropTypes.string,
    searchUrl: PropTypes.string,
    lookupUrl: PropTypes.string,
};

ResourcesSearchPage.defaultProps = {
    suggestionsUrl: '',
    searchUrl: '',
    lookupUrl: '',
};
